import {useCallback, useMemo} from 'react';
import {useCollator} from './useCollator';

export interface Filter {
  /** Returns whether a string starts with a given substring. */
  startsWith(string: string, substring: string): boolean;
  /** Returns whether a string ends with a given substring. */
  endsWith(string: string, substring: string): boolean;
  /** Returns whether a string contains a given substring. */
  contains(string: string, substring: string): boolean;
}

/**
 * Provides localized string search functionality that is useful for filtering or matching items
 * in a list. Options can be provided to adjust the sensitivity to case, diacritics, and other parameters.
 */
export function useFilter(options?: Intl.CollatorOptions): Filter {
  const collator = useCollator({
    usage: 'search',
    ...options,
  });

  // TODO(later): these methods don't currently support the ignorePunctuation option.
  const startsWith = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (string: any, substring: any) => {
      if (substring.length === 0) {
        return true;
      }

      // Normalize both strings so we can slice safely
      // TODO: take into account the ignorePunctuation option as well...
      const normalizedString = string.normalize('NFC');
      const normalizedSubstring = substring.normalize('NFC');
      return (
        collator.compare(
          normalizedString.slice(0, normalizedSubstring.length),
          normalizedSubstring,
        ) === 0
      );
    },
    [collator],
  );

  const endsWith = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (string: any, substring: any) => {
      if (substring.length === 0) {
        return true;
      }

      const normalizedString = string.normalize('NFC');
      const normalizedSubstring = substring.normalize('NFC');
      return (
        collator.compare(
          normalizedString.slice(-normalizedSubstring.length),
          normalizedSubstring,
        ) === 0
      );
    },
    [collator],
  );

  const contains = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (string: any, substring: any) => {
      if (substring.length === 0) {
        return true;
      }

      const normalizedString = string.normalize('NFC');
      const normalizedSubstring = substring.normalize('NFC');

      let scan = 0;
      const sliceLen = normalizedSubstring.length;
      for (; scan + sliceLen <= normalizedString.length; scan++) {
        const slice = normalizedString.slice(scan, scan + sliceLen);
        if (collator.compare(normalizedSubstring, slice) === 0) {
          return true;
        }
      }

      return false;
    },
    [collator],
  );

  return useMemo(
    () => ({
      startsWith,
      endsWith,
      contains,
    }),
    [startsWith, endsWith, contains],
  );
}
